import {
  getMethodTransfer,
  getListService,
  getContentPayment,
  getQrPayment,
} from '../../api';
import { useUsernameStorage, usePaymentStorage } from '../../utils/useLocalStorage';

const usernameStorage = useUsernameStorage();
const paymentStorage = usePaymentStorage();

const STEP_PAYMENT_COUNT = 5;
const STEP_SLEEP_TIME = 2000;

const setNestedField = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  let target = obj;

  keys.forEach((key) => {
    if (!target[key]) {
      target[key] = {};
    }
    target = target[key];
  });

  target[lastKey] = value;
};

const getInitialPayment = (route) => {
  const savedPayment = paymentStorage.get();
  const savedUsername = usernameStorage.get();

  return {
    username: route.query.username || savedUsername || '',
    service: {
      id: route.query.service || (savedPayment?.service?.id || ''),
    },
    method: {
      id: route.query.method || (savedPayment?.method?.id || ''),
    },
    total: 0,
    note: '',
    step: 0,
    price: 0,
    qrCode: '',
    after: {
      title: '',
      sub: '',
      btnMain: '',
      btnSub: '',
    },
  };
};

export default {
  namespaced: true,

  state: {
    loading: false,
    current: 0,
    triggerUsername: false,
    activeService: '',
    currency: 'VNĐ',
    payment: {
      username: '',
      service: {
        id: '',
      },
      method: {
        id: '',
      },
      total: 0,
      note: '',
      step: 0,
      price: 0,
      qrCode: '',
      after: {
        title: '',
        sub: '',
        btnMain: '',
        btnSub: '',
      },
    },
    option: {
      service: {},
      method: [],
    },
    steps: [
      {
        number: 0,
        id: 'service',
        title: '',
        sub: '',
        description: '',
        status: 'wait',
        icon: 'cloud-server',
      },
      {
        number: 1,
        id: 'method',
        title: '',
        sub: '',
        description: '',
        status: 'wait',
        icon: 'bank',
      },
      {
        number: 2,
        id: 'payment',
        title: '',
        sub: '',
        description: '',
        status: 'wait',
        icon: 'login',
      },
      {
        number: 3,
        id: 'finish',
        title: '',
        sub: '',
        description: '',
        status: 'wait',
        icon: 'check-circle',
      },
    ],
    confirmBox: false,
    paymentProcess: {
      show: false,
      timeWait: 1000 * 60 * 5,
      countdown: Date.now(),
    },
    paymentSuccess: '',
    pusher: {
      app: null,
      channel: null,
    },
    sleepLoading: 500,
  },

  getters: {
    paymentMethodName: (state) => {
      if (!state.payment?.method?._id) {
        return '';
      }

      return [
        state.payment.method._id,
        state.payment.method.owner,
      ].join(' / ');
    },

    paymentMethodText: (state) => {
      if (!state.payment?.method?._id) {
        return '';
      }

      return [
        `STK: <b>${state.payment.method._id}</b>`,
        state.payment.method.owner,
      ].join(' <br/> ');
    },

    paymentMethodId: (state) => {
      return state.payment?.method?._id || '';
    },

    paymentTotal: (state) => {
      const total = state.payment?.total || 0;
      const { currency } = state;
      const formattedTotal = (Math.round(total) || 0).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return [formattedTotal, currency].join(' ');
    },

    paymentNote: (state) => {
      return state.payment?.note || '';
    },

    paymentQrCode: (state) => {
      return state.payment?.qrCode || '';
    },

    findServiceData: (state) => {
      const serviceId = state.payment?.service?.id;

      if (!serviceId || !state.option.service?.length) {
        return {
          id: serviceId || '',
          name: serviceId || '',
          title: serviceId || '',
          price: 0,
        };
      }

      const allServices = (state.option.service || []).flatMap((group) => group.value);
      const serviceData = allServices.find((item) => item.id === serviceId);

      return serviceData || {
        id: serviceId,
        name: serviceId,
        title: serviceId,
        price: 0,
      };
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_CURRENT(state, current) {
      state.current = current;
    },

    SET_TRIGGER_USERNAME(state, trigger) {
      state.triggerUsername = trigger;
    },

    SET_ACTIVE_SERVICE(state, service) {
      state.activeService = service;
    },

    SET_PAYMENT(state, payment) {
      state.payment = payment;
    },

    UPDATE_PAYMENT_FIELD(state, { field, value }) {
      if (!state.payment) {
        return;
      }

      if (field.includes('.')) {
        setNestedField(state.payment, field, value);
        state.payment = { ...state.payment };
      } else {
        state.payment = {
          ...state.payment,
          [field]: value,
        };
      }
    },

    SET_OPTION_SERVICE(state, service) {
      state.option.service = service;
    },

    SET_OPTION_METHOD(state, method) {
      state.option.method = method;
    },

    SET_STEPS(state, steps) {
      state.steps = steps;
    },

    UPDATE_STEP(state, { index, data }) {
      if (state.steps[index]) {
        state.steps[index] = {
          ...state.steps[index],
          ...data,
        };
      }
    },

    SET_CONFIRM_BOX(state, show) {
      state.confirmBox = show;
    },

    SET_PAYMENT_PROCESS(state, process) {
      state.paymentProcess = {
        ...state.paymentProcess,
        ...process,
      };
    },

    SET_PAYMENT_SUCCESS(state, message) {
      state.paymentSuccess = message;
    },

    SET_PUSHER_APP(state, app) {
      state.pusher.app = app;
    },

    SET_PUSHER_CHANNEL(state, channel) {
      state.pusher.channel = channel;
    },
  },

  actions: {
    initializePayment({ commit }, { route, i18n }) {
      const payment = getInitialPayment(route);
      if (i18n) {
        payment.after = {
          title: i18n.t('PAYMENT_AFTER_TITLE'),
          sub: i18n.t('PAYMENT_AFTER_SUB'),
          btnMain: i18n.t('PAYMENT_AFTER_BTN_MAIN'),
          btnSub: i18n.t('PAYMENT_AFTER_BTN_SUB'),
        };
      }
      commit('SET_PAYMENT', payment);
    },

    initializeSteps({ commit, state }, i18n) {
      const allSteps = [
        {
          id: 'service',
          title: i18n ? i18n.t('STEP_SELECT_SERVICE') : '',
          sub: '',
          description: '',
          status: 'wait',
          icon: 'cloud-server',
        },
        {
          id: 'method',
          title: i18n ? i18n.t('STEP_SELECT_PAYMENT_METHOD') : '',
          sub: '',
          description: '',
          status: 'wait',
          icon: 'bank',
        },
        {
          id: 'payment',
          title: i18n ? i18n.t('STEP_PAYMENT_PROCCESS') : '',
          sub: '',
          description: '',
          status: 'wait',
          icon: 'login',
        },
        {
          id: 'finish',
          title: i18n ? i18n.t('STEP_PAYMENT_FINISH') : '',
          sub: '',
          description: '',
          status: 'wait',
          icon: 'check-circle',
        },
      ];

      // Skip the method-selection step when there is only one bank/method to
      // choose from. Renumber the remaining steps so `number` stays aligned
      // with the array index (PaymentSteps relies on this for status/icon).
      const skipMethod = (state.option.method || []).length === 1;

      const steps = allSteps
        .filter((step) => !(skipMethod && step.id === 'method'))
        .map((step, index) => ({
          ...step,
          number: index,
        }));

      commit('SET_STEPS', steps);
    },

    async fetchMethodTransfer({ commit }) {
      const methods = await getMethodTransfer();
      commit('SET_OPTION_METHOD', methods);
      return methods;
    },

    async fetchListService({ commit }) {
      const services = await getListService();
      commit('SET_OPTION_SERVICE', services);
      return services;
    },

    updatePaymentContent({ commit, state }) {
      const note = getContentPayment(
        state.payment?.username,
        state.payment?.service?.plan,
      );

      commit('UPDATE_PAYMENT_FIELD', {
        field: 'note',
        value: note,
      });
    },

    updateQrPayment({ commit, state }) {
      if (!state.payment?.method) {
        return;
      }

      const qrCode = getQrPayment(
        state.payment.method,
        state.payment.total,
        state.payment.note,
      );

      commit('UPDATE_PAYMENT_FIELD', {
        field: 'qrCode',
        value: qrCode,
      });
    },

    async onChangeService({ commit, dispatch, state }, item) {
      if (item.disabled) {
        return false;
      }

      commit('SET_PAYMENT', {
        ...state.payment,
        service: item,
        price: item.price,
        total: item.price,
      });

      dispatch('updatePaymentContent');
      dispatch('updateFirstStep');

      return true;
    },

    onChangeMethod({ commit, state }, item) {
      const methodIndex = state.steps.findIndex((step) => step.id === 'method');
      if (methodIndex !== -1) {
        commit('UPDATE_STEP', {
          index: methodIndex,
          data: { sub: item.code },
        });
      }

      commit('UPDATE_PAYMENT_FIELD', {
        field: 'method',
        value: item,
      });

      const qrCode = getQrPayment(item, state.payment?.total || 0, state.payment?.note || '');
      commit('UPDATE_PAYMENT_FIELD', {
        field: 'qrCode',
        value: qrCode,
      });
    },

    updateFirstStep({ commit, state }) {
      const username = state.payment?.username || '';
      const serviceName = state.payment?.service?.name || '';
      const serviceText = serviceName ? `(${serviceName})` : '';

      commit('UPDATE_STEP', {
        index: 0,
        data: {
          sub: [username, serviceText].filter((val) => val).join(' '),
        },
      });
    },

    saveUsernameToStorage({ state }) {
      if (state.payment?.username) {
        usernameStorage.set(state.payment.username);
      }
    },

    savePaymentDataToStorage({ state }) {
      if (!state.payment) {
        return;
      }

      paymentStorage.set({
        username: state.payment.username || '',
        service: { id: state.payment.service?.id || '' },
        method: { id: state.payment.method?.id || '' },
        step: state.current || 0,
      });
    },

    async runStepPayment({ commit, dispatch }, params = {}) {
      const { stepPayment = STEP_PAYMENT_COUNT, sleepTime = STEP_SLEEP_TIME } = params;

      dispatch('updatePaymentContent');
      dispatch('updateQrPayment');

      setTimeout(() => {
        dispatch('updatePaymentContent');
        dispatch('updateQrPayment');
      }, 1000);

      commit('UPDATE_PAYMENT_FIELD', {
        field: 'step',
        value: 0,
      });

      for (let index = 0; index < stepPayment; index++) {
        commit('UPDATE_PAYMENT_FIELD', {
          field: 'step',
          value: index,
        });

        await new Promise((resolve) => {
          setTimeout(resolve, sleepTime);
        });
      }
    },

    findServiceAndActiveKey({ state }) {
      const serviceId = state.payment?.service?.id;

      if (!serviceId || !state.option.service?.length) {
        return {
          findService: null,
          activeService: '',
        };
      }

      let activeService = '';
      let findService = null;

      state.option.service.forEach((group) => {
        if (!findService) {
          findService = group.value.find((service) => service.id === serviceId);
          if (findService) {
            activeService = group.name;
          }
        }
      });

      return {
        findService,
        activeService,
      };
    },
  },
};
